import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ArrowLeft, Download, Trash2, Search, Printer, FileDown, History } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/format";
import { downloadBlob } from "../lib/download";

const exportCsv = (entries) => {
  const header = ["Date", "Action", "Produit", "SKU", "Lot", "DLC", "Format", "Exemplaires", "OF", "EAN13"];
  const rows = entries.map((e) => [
    e.created_at,
    e.action,
    e.product_name,
    e.product_sku,
    e.lot_number,
    e.dlc,
    e.format_label,
    e.copies,
    e.order_reference,
    e.ean13,
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"))
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `historique-impressions-${new Date().toISOString().slice(0, 10)}.csv`);
};

export default function PrintHistory() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/print-history", { params: { limit: 1000 } });
      setEntries(data);
    } catch {
      toast.error("Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.product_name, e.product_sku, e.lot_number, e.order_reference, e.ean13]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [entries, search]);

  const totalCopies = useMemo(() => filtered.reduce((s, e) => s + (e.copies || 0), 0), [filtered]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/print-history/${id}`);
      toast.success("Entrée supprimée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete("/print-history");
      toast.success("Historique effacé");
      load();
    } catch {
      toast.error("Effacement impossible");
    }
  };

  return (
    <div className="space-y-6" data-testid="print-history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/etiquettes">
            <Button variant="ghost" size="icon" data-testid="back-to-labels">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] flex items-center gap-2">
              <History className="w-3.5 h-3.5" /> Étiquettes
            </div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">Historique des impressions</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCsv(filtered)}
            disabled={!filtered.length}
            data-testid="export-csv-btn"
          >
            <FileDown className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={!entries.length} data-testid="clear-all-btn">
                <Trash2 className="w-4 h-4 mr-2 text-[#C05C47]" /> Tout effacer
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Effacer tout l'historique ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action est irréversible. Toutes les {entries.length} entrées seront supprimées.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAll} className="bg-[#C05C47] hover:bg-[#A64A37]">
                  Effacer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Entrées</div>
          <div className="font-heading text-2xl font-medium mt-1" data-testid="stat-entries">{entries.length}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Affichées</div>
          <div className="font-heading text-2xl font-medium mt-1">{filtered.length}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Total exemplaires</div>
          <div className="font-heading text-2xl font-medium mt-1" data-testid="stat-total-copies">{totalCopies}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">PDF / Impressions</div>
          <div className="font-heading text-2xl font-medium mt-1">
            {filtered.filter((e) => e.action === "PDF").length} / {filtered.filter((e) => e.action === "PRINT").length}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg">
        <div className="p-4 border-b border-[#E5E5E0] flex items-center gap-2">
          <Search className="w-4 h-4 text-[#575A57]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par produit, SKU, lot, OF, EAN13…"
            className="border-0 focus-visible:ring-0 px-0"
            data-testid="search-input"
          />
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#575A57]">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#A3A6A3]">
            {entries.length === 0
              ? "Aucune impression enregistrée pour l'instant."
              : "Aucun résultat pour cette recherche."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#575A57]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Action</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Produit</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Lot</th>
                  <th className="text-left px-4 py-2.5 font-semibold">OF</th>
                  <th className="text-left px-4 py-2.5 font-semibold">DLC</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Format</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Exempl.</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t border-[#F0F0EB] hover:bg-[#FBFBF8]" data-testid={`entry-${e.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-[#575A57]">{fmtDateTime(e.created_at)}</td>
                    <td className="px-4 py-2.5">
                      {e.action === "PDF" ? (
                        <Badge variant="outline" className="bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D] gap-1">
                          <Download className="w-3 h-3" /> PDF
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-[#F0F0EB] border-[#E5E5E0] text-[#1A1C1A] gap-1">
                          <Printer className="w-3 h-3" /> Impr.
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-[#1A1C1A]">{e.product_name}</div>
                      <div className="text-xs font-mono text-[#575A57]">{e.product_sku}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      {e.lot_number ? (
                        <Link
                          to={`/tracabilite?lot=${encodeURIComponent(e.lot_number)}`}
                          className="font-mono text-[#1F4E3D] hover:underline"
                          data-testid={`lot-link-${e.id}`}
                        >
                          {e.lot_number}
                        </Link>
                      ) : (
                        <span className="text-[#A3A6A3]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{e.order_reference || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{e.dlc || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-[#575A57]">{e.format_label}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{e.copies}</td>
                    <td className="px-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(e.id)}
                        data-testid={`delete-${e.id}`}
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
